import React, { useState, useMemo, useCallback } from 'react';
import { Select, Card, Space, Typography, Tag, Tooltip, Empty, Button, message } from 'antd';
import { SwapOutlined, TableOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
  classifyFields,
  computePivot,
  computeTotals,
  AGGREGATION_TYPES,
} from '../utils/pivotUtils';
import DrillDownModal from './DrillDownModal';

const { Text } = Typography;
const { Option, OptGroup } = Select;

const DATE_GRANULARITIES = [
  { value: 'year', labelKey: 'reports.pivotDateYear' },
  { value: 'quarter', labelKey: 'reports.pivotDateQuarter' },
  { value: 'month', labelKey: 'reports.pivotDateMonth' },
  { value: 'day', labelKey: 'reports.pivotDateDay' },
];

const SYSTEM_FIELDS = [
  { fieldId: 'formName', displayLabel: null, labelKey: 'common.formName', isDate: false },
  { fieldId: 'status', displayLabel: null, labelKey: 'common.status', isDate: false },
  { fieldId: 'fillType', displayLabel: null, labelKey: 'common.fillType', isDate: false },
  { fieldId: 'createdAt', displayLabel: null, labelKey: 'common.createdAt', isDate: true },
];

const EFormPivotReport = ({ dataSource }) => {
  const { t } = useLanguage();

  const [measureKey, setMeasureKey] = useState('__count__');
  const [rowDimKey, setRowDimKey] = useState('sys:formName');
  const [rowDateGranularity, setRowDateGranularity] = useState('month');
  const [colDimKey, setColDimKey] = useState('sys:createdAt');
  const [colDateGranularity, setColDateGranularity] = useState('quarter');

  const [drillDown, setDrillDown] = useState({ open: false, instanceIds: [], title: '' });

  const { measureFields, dimensionFields, dateFields, allFields } = useMemo(
    () => classifyFields(dataSource),
    [dataSource]
  );

  const parseDimKey = useCallback(
    (key) => {
      if (!key) return null;
      if (key.startsWith('sys:')) {
        const sysId = key.slice(4);
        const sf = SYSTEM_FIELDS.find((f) => f.fieldId === sysId);
        return sf ? { fieldId: sysId, isSystemField: true, isDate: sf.isDate } : null;
      }
      const field = allFields.find((f) => f.fieldId === key);
      if (!field) return null;
      return {
        fieldId: key,
        isSystemField: false,
        isDate: field.inputType === 'date',
      };
    },
    [allFields]
  );

  const measure = useMemo(() => {
    if (measureKey === '__count__') return { type: AGGREGATION_TYPES.COUNT };
    return { type: AGGREGATION_TYPES.SUM, fieldId: measureKey };
  }, [measureKey]);

  /** 表單欄位顯示名稱：優先 display name，方便用戶辨識 */
  const getFieldDisplayName = (f) => (f?.displayLabel || f?.originalLabel || f?.fieldId || '');

  /** 下拉選項顯示：display name + html input name (fieldId)，方便對應表單欄位 */
  const getFieldOptionLabel = (f, suffix = '') => {
    const displayName = getFieldDisplayName(f);
    const name = f?.fieldId || '';
    if (!name) return (displayName || '') + suffix;
    const label = displayName ? `${displayName} (${name})` : name;
    return label + suffix;
  };

  const measureLabel = useMemo(() => {
    if (measureKey === '__count__') return t('reports.pivotCount') || 'Count';
    const f = measureFields.find((mf) => mf.fieldId === measureKey);
    return f ? getFieldDisplayName(f) : measureKey;
  }, [measureKey, measureFields, t]);

  const rowDim = useMemo(() => {
    const dim = parseDimKey(rowDimKey);
    if (dim?.isDate) dim.dateGranularity = rowDateGranularity;
    return dim;
  }, [rowDimKey, rowDateGranularity, parseDimKey]);

  const colDim = useMemo(() => {
    const dim = parseDimKey(colDimKey);
    if (dim?.isDate) dim.dateGranularity = colDateGranularity;
    return dim;
  }, [colDimKey, colDateGranularity, parseDimKey]);

  const { rows, cols, cells } = useMemo(
    () => computePivot(dataSource, rowDim, colDim, measure),
    [dataSource, rowDim, colDim, measure]
  );

  const { rowTotals, colTotals, grandTotal } = useMemo(
    () => computeTotals(rows, cols, cells),
    [rows, cols, cells]
  );

  const handleCellClick = (instanceIds, rowKey, colKey) => {
    if (!instanceIds || instanceIds.length === 0) return;
    const title = `${rowKey} × ${colKey} — ${instanceIds.length} ${t('reports.pivotRecords') || '筆'}`;
    setDrillDown({ open: true, instanceIds, title });
  };

  const getDimLabel = (key) => {
    if (!key) return '';
    if (key.startsWith('sys:')) {
      const sysId = key.slice(4);
      const sf = SYSTEM_FIELDS.find((f) => f.fieldId === sysId);
      return sf ? (t(sf.labelKey) || sf.fieldId) : sysId;
    }
    const f = allFields.find((af) => af.fieldId === key);
    return f ? getFieldDisplayName(f) : key;
  };

  const isRowDate = rowDim?.isDate;
  const isColDate = colDim?.isDate;

  const buildDimOptions = () => {
    const sysOptions = SYSTEM_FIELDS.map((sf) => ({
      value: `sys:${sf.fieldId}`,
      label: `${t(sf.labelKey) || sf.fieldId} (${sf.fieldId})`,
    }));
    const formOptions = dimensionFields.map((f) => ({
      value: f.fieldId,
      label: getFieldOptionLabel(f, f.inputType === 'date' ? ' 📅' : ''),
    }));
    return { sysOptions, formOptions };
  };

  const { sysOptions, formOptions } = buildDimOptions();

  const formatValue = (val) => {
    if (val == null) return '';
    if (measure.type === AGGREGATION_TYPES.COUNT) return val.toLocaleString();
    return typeof val === 'number' ? val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : val;
  };

  const handleExportExcel = useCallback(() => {
    try {
      const totalLabel = t('reports.pivotTotal') || 'Total';
      const headerRow = [getDimLabel(rowDimKey) + ' \\ ' + getDimLabel(colDimKey), ...cols, totalLabel];
      const totalRow = [totalLabel, ...cols.map((ck) => colTotals[ck]?.value ?? ''), grandTotal.value];
      const dataRows = rows.map((rk) => [
        rk,
        ...cols.map((ck) => cells[rk]?.[ck]?.value ?? ''),
        rowTotals[rk]?.value ?? '',
      ]);
      const aoa = [headerRow, totalRow, ...dataRows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pivot');
      const fileName = `eform_pivot_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
      message.success(t('reports.exportExcelSuccess') || '已匯出 Excel');
    } catch (err) {
      console.error('Export Excel failed:', err);
      message.error(t('reports.exportExcelFailed') || '匯出 Excel 失敗');
    }
  }, [rows, cols, cells, rowTotals, colTotals, grandTotal, getDimLabel, rowDimKey, colDimKey, t]);

  if (!dataSource || dataSource.length === 0) {
    return (
      <Empty
        description={t('reports.eformInstancesNoData') || '沒有符合條件的表單資料'}
        style={{ padding: 60 }}
      />
    );
  }

  return (
    <div>
      {/* Config bar */}
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          {/* Measure */}
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
              {t('reports.pivotMeasure') || '度量 (Measure)'}
            </Text>
            <Select
              value={measureKey}
              onChange={setMeasureKey}
              style={{ minWidth: 180 }}
              popupMatchSelectWidth={false}
            >
              <Option value="__count__">
                <Tag color="blue">Count</Tag> {t('reports.pivotCount') || '筆數'}
              </Option>
              {measureFields.map((f) => (
                <Option key={f.fieldId} value={f.fieldId}>
                  <Tag color={f.inputType === 'currency' ? 'gold' : 'green'}>
                    {f.inputType === 'currency' ? '$' : '#'}
                  </Tag>{' '}
                  {getFieldOptionLabel(f)}
                </Option>
              ))}
            </Select>
          </div>

          {/* Row dimension */}
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
              {t('reports.pivotRowDimension') || '列維度 (Rows)'}
            </Text>
            <Space.Compact>
              <Select
                value={rowDimKey}
                onChange={(v) => setRowDimKey(v)}
                style={{ minWidth: 180 }}
                popupMatchSelectWidth={false}
              >
                <OptGroup label={t('reports.pivotSystemFields') || '系統欄位'}>
                  {sysOptions.map((o) => (
                    <Option key={o.value} value={o.value}>{o.label}</Option>
                  ))}
                </OptGroup>
                {formOptions.length > 0 && (
                  <OptGroup label={t('reports.pivotFormFields') || '表單欄位'}>
                    {formOptions.map((o) => (
                      <Option key={o.value} value={o.value}>{o.label}</Option>
                    ))}
                  </OptGroup>
                )}
              </Select>
              {isRowDate && (
                <Select value={rowDateGranularity} onChange={setRowDateGranularity} style={{ width: 100 }}>
                  {DATE_GRANULARITIES.map((g) => (
                    <Option key={g.value} value={g.value}>{t(g.labelKey) || g.value}</Option>
                  ))}
                </Select>
              )}
            </Space.Compact>
          </div>

          {/* Swap button */}
          <Tooltip title={t('reports.pivotSwapAxes') || '交換列/欄'}>
            <div
              onClick={() => {
                setRowDimKey(colDimKey);
                setColDimKey(rowDimKey);
                setRowDateGranularity(colDateGranularity);
                setColDateGranularity(rowDateGranularity);
              }}
              style={{ cursor: 'pointer', padding: '6px 8px', borderRadius: 6, border: '1px solid #d9d9d9', display: 'flex', alignItems: 'center', height: 32 }}
            >
              <SwapOutlined />
            </div>
          </Tooltip>

          {/* Column dimension */}
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
              {t('reports.pivotColDimension') || '欄維度 (Columns)'}
            </Text>
            <Space.Compact>
              <Select
                value={colDimKey}
                onChange={(v) => setColDimKey(v)}
                style={{ minWidth: 180 }}
                popupMatchSelectWidth={false}
              >
                <OptGroup label={t('reports.pivotSystemFields') || '系統欄位'}>
                  {sysOptions.map((o) => (
                    <Option key={o.value} value={o.value}>{o.label}</Option>
                  ))}
                </OptGroup>
                {formOptions.length > 0 && (
                  <OptGroup label={t('reports.pivotFormFields') || '表單欄位'}>
                    {formOptions.map((o) => (
                      <Option key={o.value} value={o.value}>{o.label}</Option>
                    ))}
                  </OptGroup>
                )}
              </Select>
              {isColDate && (
                <Select value={colDateGranularity} onChange={setColDateGranularity} style={{ width: 100 }}>
                  {DATE_GRANULARITIES.map((g) => (
                    <Option key={g.value} value={g.value}>{t(g.labelKey) || g.value}</Option>
                  ))}
                </Select>
              )}
            </Space.Compact>
          </div>
        </div>
      </Card>

      {/* Pivot table */}
      <Card
        size="small"
        title={
          <Space>
            <TableOutlined />
            <span>{measureLabel}</span>
          </Space>
        }
        extra={
          <Button type="primary" ghost icon={<FileExcelOutlined />} onClick={handleExportExcel}>
            {t('reports.exportExcel') || '匯出 Excel'}
          </Button>
        }
        bodyStyle={{ padding: 0, overflow: 'auto' }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              minWidth: Math.max(600, (cols.length + 2) * 110),
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: '#fafafa' }}>
                <th style={thStyle}>
                  {getDimLabel(rowDimKey)} \ {getDimLabel(colDimKey)}
                </th>
                {cols.map((ck) => (
                  <th key={ck} style={thStyle}>{ck}</th>
                ))}
                <th style={{ ...thStyle, backgroundColor: '#f0f0f0', fontWeight: 700 }}>
                  {t('reports.pivotTotal') || 'Total'}
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Total row */}
              <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 600 }}>
                <td style={tdLabelStyle}>{t('reports.pivotTotal') || 'Total'}</td>
                {cols.map((ck) => (
                  <td
                    key={ck}
                    style={tdValueStyle}
                    onClick={() => handleCellClick(colTotals[ck]?.instanceIds, 'Total', ck)}
                  >
                    {formatValue(colTotals[ck]?.value || 0)}
                  </td>
                ))}
                <td
                  style={{ ...tdValueStyle, backgroundColor: '#e8e8e8', fontWeight: 700 }}
                  onClick={() => handleCellClick(grandTotal.instanceIds, 'Total', 'Total')}
                >
                  {formatValue(grandTotal.value)}
                </td>
              </tr>
              {/* Data rows */}
              {rows.map((rk) => (
                <tr key={rk}>
                  <td style={tdLabelStyle}>{rk}</td>
                  {cols.map((ck) => {
                    const cell = cells[rk]?.[ck];
                    return (
                      <td
                        key={ck}
                        style={{
                          ...tdValueStyle,
                          color: cell?.value ? '#333' : '#ccc',
                          cursor: cell?.instanceIds?.length ? 'pointer' : 'default',
                        }}
                        onClick={() => cell?.instanceIds?.length && handleCellClick(cell.instanceIds, rk, ck)}
                      >
                        {cell?.value ? formatValue(cell.value) : ''}
                      </td>
                    );
                  })}
                  <td
                    style={{ ...tdValueStyle, backgroundColor: '#fafafa', fontWeight: 600 }}
                    onClick={() => handleCellClick(rowTotals[rk]?.instanceIds, rk, 'Total')}
                  >
                    {formatValue(rowTotals[rk]?.value || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <DrillDownModal
        open={drillDown.open}
        onClose={() => setDrillDown({ open: false, instanceIds: [], title: '' })}
        title={drillDown.title}
        instanceIds={drillDown.instanceIds}
        dataSource={dataSource}
      />
    </div>
  );
};

const thStyle = {
  padding: '10px 12px',
  textAlign: 'center',
  borderBottom: '2px solid #e8e8e8',
  borderRight: '1px solid #f0f0f0',
  whiteSpace: 'nowrap',
  fontSize: 13,
  fontWeight: 600,
  position: 'sticky',
  top: 0,
  backgroundColor: '#fafafa',
};

const tdLabelStyle = {
  padding: '8px 12px',
  borderBottom: '1px solid #f0f0f0',
  borderRight: '1px solid #f0f0f0',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  backgroundColor: '#fafafa',
  position: 'sticky',
  left: 0,
};

const tdValueStyle = {
  padding: '8px 12px',
  textAlign: 'right',
  borderBottom: '1px solid #f0f0f0',
  borderRight: '1px solid #f0f0f0',
  cursor: 'pointer',
  transition: 'background-color 0.15s',
};

export default EFormPivotReport;
